
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import type { AssistantFormData } from "./types";
import { getOpenAIClient } from "@/lib/openai";

export const useAssistantUpdate = (onSuccess: () => void) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);

  const updateAssistant = async (assistantId: string, formData: AssistantFormData) => {
    console.log('Updating assistant:', { assistantId, formData });
    setIsLoading(true);

    try {
      const openai = await getOpenAIClient();

      // Get the existing assistant data
      const { data: existingAssistant } = await supabase
        .from('assistants')
        .select('assistant_id, vector_store_id')
        .eq('id', assistantId)
        .single();

      if (!existingAssistant) throw new Error('Assistant not found');

      // Get all file IDs for the assistant
      const { data: fileIds } = await supabase
        .from('assistant_files')
        .select('openai_file_id')
        .eq('assistant_id', assistantId);

      console.log('Updating OpenAI assistant...');
      const updateParams: any = {
        name: formData.name,
        instructions: formData.instructions,
        model: formData.model,
      };

      // Add file_search tool if we have a vector store
      if (existingAssistant.vector_store_id) {
        const { data: vectorStore } = await supabase
          .from('vector_stores')
          .select('openai_vector_store_id')
          .eq('id', existingAssistant.vector_store_id)
          .single();

        if (vectorStore) {
          updateParams.tools = [{ type: "file_search" }];
          updateParams.tool_resources = {
            file_search: {
              vector_store_ids: [vectorStore.openai_vector_store_id]
            }
          };
        }
      }

      const assistant = await openai.beta.assistants.update(
        existingAssistant.assistant_id,
        updateParams
      );

      console.log('Assistant updated:', assistant);

      // Update Supabase record
      const { error: dbError } = await supabase
        .from('assistants')
        .update({
          name: formData.name,
          description: formData.instructions,
          model: formData.model,
          creativity: formData.creativity,
          updated_at: new Date().toISOString()
        })
        .eq('id', assistantId);

      if (dbError) throw dbError;

      console.log('Assistant updated in database');
      toast.success("Assistant updated successfully");
      onSuccess();
    } catch (error) {
      console.error('Error updating assistant:', error);
      toast.error(error instanceof Error ? error.message : "Failed to update assistant");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    isEnhancing,
    updateAssistant,
  };
};
